/* ═══════════════════════════════════════════════════════════
   js/components/modal-dialog.js — <modal-dialog> Web Component
   Focus trap, Escape/overlay close, ARIA
   ═══════════════════════════════════════════════════════════ */
class ModalDialog extends HTMLElement {
  constructor() {
    super();
    this.attachShadow({ mode: 'open' });
    this._onKey = this._onKey.bind(this);
    this._onOverlay = this._onOverlay.bind(this);
  }

  static get observedAttributes() { return ['open', 'data-title']; }

  connectedCallback() {
    this.shadowRoot.innerHTML = `
      <style>
        :host { display: none; position: fixed; inset: 0; z-index: 9999; }
        :host([open]) { display: flex; align-items: center; justify-content: center; }
        .overlay { position: absolute; inset: 0; background: rgba(0,0,0,.7); backdrop-filter: blur(4px); }
        .dialog {
          position: relative; z-index: 1;
          background: var(--dark2, #12121a); border: 1px solid var(--border, rgba(255,255,255,.08));
          border-radius: 16px; padding: 24px; max-width: 520px; width: 90vw;
          max-height: 85vh; overflow-y: auto; color: var(--text, #e8e6e1);
          box-shadow: 0 20px 60px rgba(0,0,0,.5);
        }
        .header { display: flex; align-items: center; justify-content: space-between; margin-bottom: 16px; }
        .title { font-family: var(--font-display, 'Unbounded', sans-serif); font-size: 18px; font-weight: 800; color: var(--gold, #c8a96e); }
        .close {
          background: none; border: 1px solid var(--border, rgba(255,255,255,.08));
          color: var(--text-muted, #8a8a8a); width: 36px; height: 36px; border-radius: 10px;
          font-size: 18px; cursor: pointer; display: flex; align-items: center; justify-content: center;
          transition: all .2s;
        }
        .close:hover { color: var(--gold, #c8a96e); border-color: var(--gold, #c8a96e); }
      </style>
      <div class="overlay" part="overlay"></div>
      <div class="dialog" role="dialog" aria-modal="true" aria-labelledby="modal-title" part="dialog">
        <div class="header">
          <span class="title" id="modal-title">${this.getAttribute('data-title') || ''}</span>
          <button class="close" aria-label="Закрыть">&times;</button>
        </div>
        <slot></slot>
      </div>
    `;

    this.shadowRoot.querySelector('.close').addEventListener('click', () => this.close());
    this.shadowRoot.querySelector('.overlay').addEventListener('click', this._onOverlay);
  }

  attributeChangedCallback(name, oldVal, newVal) {
    if (name === 'open') {
      if (newVal !== null) {
        this._open();
      } else {
        this._close();
      }
    }
    if (name === 'data-title') {
      const el = this.shadowRoot?.getElementById('modal-title');
      if (el) el.textContent = newVal || '';
    }
  }

  open() { this.setAttribute('open', ''); }

  close() {
    this.removeAttribute('open');
    this.dispatchEvent(new CustomEvent('modal-close', { bubbles: true }));
  }

  _open() {
    document.addEventListener('keydown', this._onKey);
    document.body.style.overflow = 'hidden';
    // Focus first focusable element
    requestAnimationFrame(() => {
      const focusable = this.querySelector('input, button, select, textarea, [tabindex]')
                     || this.shadowRoot.querySelector('.close');
      if (focusable) focusable.focus();
    });
  }

  _close() {
    document.removeEventListener('keydown', this._onKey);
    document.body.style.overflow = '';
  }

  _onKey(e) {
    if (e.key === 'Escape') { this.close(); return; }
    // Focus trap
    if (e.key === 'Tab') {
      const focusable = [...this.querySelectorAll('input, button, select, textarea, a, [tabindex]'),
                         ...this.shadowRoot.querySelectorAll('button')];
      if (!focusable.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    }
  }

  _onOverlay(e) {
    if (e.target === this.shadowRoot.querySelector('.overlay')) this.close();
  }

  disconnectedCallback() {
    document.removeEventListener('keydown', this._onKey);
    document.body.style.overflow = '';
  }
}

if (!customElements.get('modal-dialog')) {
  customElements.define('modal-dialog', ModalDialog);
}
